import React, { Suspense } from "react";
import { Popover, Spin } from "antd";
import type { EmojiClickData, Theme } from "emoji-picker-react";
import { CHAT_MODAL_Z_INDEX } from "./types";

// Loaded only once a picker is actually opened — the picker's own data +
// Flairup runtime is a few hundred KB, not worth it on every mount.
// Declared at module scope so React.lazy's component identity is stable
// across renders (re-creating it per-render would remount/reload it).
const LazyEmojiPicker = React.lazy(() => import("emoji-picker-react"));

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEmojiClick: (data: EmojiClickData) => void;
  placement?: "top" | "topLeft" | "topRight" | "bottom" | "bottomLeft" | "bottomRight";
  children: React.ReactNode;
};

// Shared by the composer's "insert emoji into text" tray button and
// MessageBubble's "react to this message" button — same picker, different
// onEmojiClick behavior.
const EmojiPickerPopover: React.FC<Props> = ({ open, onOpenChange, onEmojiClick, placement = "topRight", children }) => (
  <Popover
    trigger="click"
    open={open}
    onOpenChange={onOpenChange}
    placement={placement}
    zIndex={CHAT_MODAL_Z_INDEX}
    overlayInnerStyle={{ padding: 0 }}
    content={
      <Suspense
        fallback={
          <div className="chat-composer__emoji-loading">
            <Spin size="small" />
          </div>
        }
      >
        {/* theme is cast rather than imported as a value — the runtime `Theme` enum lives in
            the same module as the lazy-loaded default export, so importing it normally would
            pull that module in eagerly and defeat the point of React.lazy above. */}
        <LazyEmojiPicker theme={"light" as Theme} width={300} height={360} previewConfig={{ showPreview: false }} onEmojiClick={onEmojiClick} />
      </Suspense>
    }
  >
    {children}
  </Popover>
);

export default EmojiPickerPopover;
